import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import StudyResourceService from '../../_services/StudyResourceService';

function isS3Url(url: string): boolean {
  return url.includes('.s3.') && url.includes('amazonaws.com');
}

function extractFilename(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const parts = pathname.split('/');
    const filename = parts[parts.length - 1];
    const match = filename.match(/^\d+-(.+)$/);
    return match ? decodeURIComponent(match[1]) : decodeURIComponent(filename);
  } catch {
    return 'Attachment';
  }
}

const ArticleViewerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [resource, setResource] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    StudyResourceService.getStudyResourceById(Number(id))
      .then(resp => {
        if (resp.success && resp.data) {
          setResource(resp.data);
        } else {
          setError('Resource not found');
        }
      })
      .catch(() => setError('Failed to load resource'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDownload() {
    if (!resource?.url) return;
    setDownloading(true);
    try {
      if (isS3Url(resource.url)) {
        const newWindow = window.open('', '_blank');
        const signedUrl = await StudyResourceService.getSignedUrl(resource.url);
        if (newWindow) newWindow.location.href = signedUrl;
      } else {
        window.open(resource.url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading...
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: 'var(--status-danger)', marginBottom: '16px' }}>{error || 'Resource not found'}</div>
        <button className="btn btn-secondary btn-sm" onClick={() => history.goBack()}>Go Back</button>
      </div>
    );
  }

  const filename = resource.url ? extractFilename(resource.url) : null;

  return (
    <div style={{ padding: '32px 24px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Back link */}
      <button
        onClick={() => history.goBack()}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--brand-primary)',
          fontSize: '14px',
          cursor: 'pointer',
          padding: 0,
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        ← Back to Resources
      </button>

      {/* Title */}
      <h4 style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: '8px' }}>
        {resource.name}
      </h4>
      <span style={{ fontSize: '11px', color: 'var(--text-faint)', fontFamily: 'monospace' }}>#{resource.id}</span>

      {/* Divider */}
      <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: '20px 0' }} />

      {/* Article Content */}
      {resource.content ? (
        <div
          style={{
            fontSize: '15px',
            lineHeight: 1.8,
            color: 'var(--text-strong)',
            whiteSpace: 'pre-wrap',
          }}
        >
          {resource.content}
        </div>
      ) : (
        <div style={{ color: 'var(--text-faint)', fontStyle: 'italic', padding: '20px 0' }}>
          No article content available.
        </div>
      )}

      {/* Attachment Section */}
      {resource.url && (
        <>
          <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: '28px 0 20px' }} />
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '14px 16px',
            background: 'var(--surface-page)',
            borderRadius: '8px',
            border: '1px solid var(--border-default)',
          }}>
            <span style={{ fontSize: '20px' }}>📎</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {filename}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Attachment</div>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleDownload}
              disabled={downloading}
              style={{ fontSize: '12px', padding: '5px 14px' }}
            >
              {downloading ? 'Opening...' : 'Download'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleViewerPage;
